// Servicio para gestión de inventario con Firestore
// Basado en la guía del Sistema de Gestión de Stock

// Las funciones de Firestore se obtienen desde window.auth

// Interfaces según la estructura de la guía
export interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  createdAt: any;
  updatedAt: any;
}

export interface Brand {
  id: string;
  name: string;
  description: string;
  website?: string;
  contactEmail?: string;
  createdAt: any;
  updatedAt: any;
}

export interface Supplier {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  paymentTerms: string;
  notes?: string;
  createdAt: any;
  updatedAt: any;
}

export interface Category {
  id: string;
  name: string;
  fields: Record<string, any>;
  enabled: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface Product {
  id: string;
  name: string;
  category: string;
  stock: number;
  priceCost: number;
  priceSale: number;
  brand: string;
  supplier: string;
  imageURL?: string;
  fechaIngreso: string;
  sku: string;
  data: Record<string, any>;
  createdAt: any;
  updatedAt: any;
}

export interface InventoryStats {
  totalProducts: number;
  totalValue: number;
  lowStockProducts: number;
  categoriesCount: number;
  brandsCount: number;
  suppliersCount: number;
  lastUpdated: any;
  topCategories: Array<{
    name: string;
    count: number;
    value: number;
  }>;
  lowStockItems: Array<{
    name: string;
    sku: string;
    stock: number;
    category: string;
  }>;
  recentActivity: Array<{
    action: string;
    product: string;
    date: string;
    type: 'add' | 'update' | 'delete';
  }>;
}

class InventoryService {
  private db: any;
  private auth: any;
  private storage: any;
  private collection: any;
  private doc: any;
  private getDoc: any;
  private setDoc: any;
  private updateDoc: any;
  private addDoc: any;
  private deleteDoc: any;
  private getDocs: any;
  private query: any;
  private orderBy: any;
  private where: any;
  private serverTimestamp: any;

  constructor() {
    // No inicializar aquí, se hará dinámicamente
  }

  private ensureFirebaseInitialized() {
    if (!this.db || !this.auth) {
      // Obtener instancias de Firebase desde el objeto global
      if (typeof window !== 'undefined' && (window as any).auth) {
        this.db = (window as any).auth.db;
        this.auth = (window as any).auth.auth;
        this.storage = (window as any).auth.storage;
        this.collection = (window as any).auth.collection;
        this.doc = (window as any).auth.doc;
        this.getDoc = (window as any).auth.getDoc;
        this.setDoc = (window as any).auth.setDoc;
        this.updateDoc = (window as any).auth.updateDoc;
        this.addDoc = (window as any).auth.addDoc;
        this.deleteDoc = (window as any).auth.deleteDoc;
        this.getDocs = (window as any).auth.getDocs;
        this.query = (window as any).auth.query;
        this.orderBy = (window as any).auth.orderBy;
        this.where = (window as any).auth.where;
        this.serverTimestamp = (window as any).auth.serverTimestamp;
        
        console.log('🔧 Firebase inicializado dinámicamente para inventario:', {
          hasDb: !!this.db,
          hasAuth: !!this.auth,
          hasCollection: !!this.collection,
          hasDoc: !!this.doc,
          hasSetDoc: !!this.setDoc,
          hasUpdateDoc: !!this.updateDoc,
          hasAddDoc: !!this.addDoc,
          hasDeleteDoc: !!this.deleteDoc,
          hasGetDocs: !!this.getDocs,
          hasQuery: !!this.query,
          hasOrderBy: !!this.orderBy,
          hasWhere: !!this.where,
          hasServerTimestamp: !!this.serverTimestamp
        });
      } else {
        throw new Error('Firebase no está disponible. Asegúrate de que auth.js esté cargado.');
      }
    }
  }

  private getUserId(): string {
    this.ensureFirebaseInitialized();
    
    // Intentar obtener el usuario desde window.auth (sistema actual)
    if (this.auth && this.auth.currentUser) {
      return this.auth.currentUser.uid;
    }
    
    // Fallback a localStorage si está disponible
    const userStr = localStorage.getItem('firebase:authUser');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user && user.uid) {
          return user.uid;
        }
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    }
    
    throw new Error('Usuario no autenticado');
  }

  private getStandardizedId(categoryName: string): string {
    const mapping: Record<string, string> = {
      'CAMPERA': 'campera',
      'BUZO': 'buzo',
      'CAMISA': 'camisa',
      'REMERA': 'remera',
      'PANTALÓN': 'pantalon',
      'POLLERA': 'pollera',
      'VESTIDO': 'vestido',
      'PAÑUELO': 'pañuelo',
      'SOMBRERO, GORRA, GORRO': 'sgg',
      'CALZADO': 'calzado'
    };
    return mapping[categoryName] || categoryName.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private getUserCollection(collectionName: string) {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    return this.collection(this.db, 'users', userId, collectionName);
  }

  // ===== GESTIÓN DE PERFIL DE USUARIO =====
  async createUserProfile(profileData: { email: string; displayName?: string }): Promise<void> {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    const userRef = this.doc(this.db, 'users', userId);
    
    await this.setDoc(userRef, {
      id: userId,
      email: profileData.email,
      displayName: profileData.displayName || '',
      createdAt: this.serverTimestamp(),
      updatedAt: this.serverTimestamp()
    });
  }

  async getUserProfile(): Promise<UserProfile | null> {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    const userRef = this.doc(this.db, 'users', userId);
    const doc = await this.getDoc(userRef);
    
    if (doc.exists()) {
      return { id: doc.id, ...doc.data() } as UserProfile;
    }
    return null;
  }

  // ===== GESTIÓN DE MARCAS =====
  async addBrand(brandData: Omit<Brand, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    this.ensureFirebaseInitialized();
    const brandsRef = this.getUserCollection('brands');
    
    const docRef = await this.addDoc(brandsRef, {
      ...brandData,
      createdAt: this.serverTimestamp(),
      updatedAt: this.serverTimestamp()
    });
    
    return docRef.id;
  }

  async getBrands(): Promise<Brand[]> {
    this.ensureFirebaseInitialized();
    const brandsRef = this.collection(this.db, 'users', this.getUserId(), 'brands');
    const snapshot = await this.getDocs(brandsRef);
    
    const brands = snapshot.docs.map((doc: any) => ({
      id: doc.id,
      ...doc.data()
    } as Brand));
    
    // Ordenar manualmente por fecha de creación
    return brands.sort((a: Brand, b: Brand) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    });
  }

  async updateBrand(brandId: string, brandData: Partial<Brand>): Promise<void> {
    this.ensureFirebaseInitialized();
    const brandRef = this.doc(this.db, 'users', this.getUserId(), 'brands', brandId);
    await this.updateDoc(brandRef, {
      ...brandData,
      updatedAt: this.serverTimestamp()
    });
  }

  async deleteBrand(brandId: string): Promise<void> {
    this.ensureFirebaseInitialized();
    const { doc, deleteDoc } = (window as any).auth;
    const brandRef = doc(this.db, 'users', this.getUserId(), 'brands', brandId);
    await deleteDoc(brandRef);
  }

  // ===== GESTIÓN DE PROVEEDORES =====
  async addSupplier(supplierData: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    this.ensureFirebaseInitialized();
    const suppliersRef = this.getUserCollection('suppliers');
    
    const docRef = await this.addDoc(suppliersRef, {
      ...supplierData,
      createdAt: this.serverTimestamp(),
      updatedAt: this.serverTimestamp()
    });
    
    return docRef.id;
  }

  async getSuppliers(): Promise<Supplier[]> {
    this.ensureFirebaseInitialized();
    const suppliersRef = this.collection(this.db, 'users', this.getUserId(), 'suppliers');
    const snapshot = await this.getDocs(suppliersRef);
    
    const suppliers = snapshot.docs.map((doc: any) => ({
      id: doc.id,
      ...doc.data()
    } as Supplier));
    
    // Ordenar manualmente por fecha de creación
    return suppliers.sort((a: Supplier, b: Supplier) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    });
  }

  async updateSupplier(supplierId: string, supplierData: Partial<Supplier>): Promise<void> {
    this.ensureFirebaseInitialized();
    const supplierRef = this.doc(this.db, 'users', this.getUserId(), 'suppliers', supplierId);
    await this.updateDoc(supplierRef, {
      ...supplierData,
      updatedAt: this.serverTimestamp()
    });
  }

  async deleteSupplier(supplierId: string): Promise<void> {
    this.ensureFirebaseInitialized();
    const { doc, deleteDoc } = (window as any).auth;
    const supplierRef = doc(this.db, 'users', this.getUserId(), 'suppliers', supplierId);
    await deleteDoc(supplierRef);
  }

  // ===== GESTIÓN DE CATEGORÍAS =====
  async addCategory(categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    this.ensureFirebaseInitialized();
    const categoriesRef = this.getUserCollection('categories');
    
    const docRef = await this.addDoc(categoriesRef, {
      ...categoryData,
      createdAt: this.serverTimestamp(),
      updatedAt: this.serverTimestamp()
    });
    
    return docRef.id;
  }

  async getCategories(): Promise<Category[]> {
    this.ensureFirebaseInitialized();
    const categoriesRef = this.collection(this.db, 'users', this.getUserId(), 'categories');
    const snapshot = await this.getDocs(categoriesRef);
    
    const categories = snapshot.docs.map((doc: any) => ({
      id: doc.id,
      ...doc.data()
    } as Category));
    
    // Ordenar manualmente por nombre
    return categories.sort((a: Category, b: Category) => a.name.localeCompare(b.name));
  }

  async updateCategory(categoryId: string, categoryData: Partial<Category>): Promise<void> {
    this.ensureFirebaseInitialized();
    const categoryRef = this.doc(this.db, 'users', this.getUserId(), 'categories', categoryId);
    await this.updateDoc(categoryRef, {
      ...categoryData,
      updatedAt: this.serverTimestamp()
    });
  }

  async deleteCategory(categoryId: string): Promise<void> {
    this.ensureFirebaseInitialized();
    const { doc, deleteDoc } = (window as any).auth;
    const categoryRef = doc(this.db, 'users', this.getUserId(), 'categories', categoryId);
    await deleteDoc(categoryRef);
  }

  // ===== GESTIÓN DE PRODUCTOS CON ESTRUCTURA DOCUMENTADA =====
  async getProductsFromDocumentedStructure(): Promise<Product[]> {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    
    try {
      // Usar funciones de Firestore desde window.auth
      const { collection, getDocs } = (window as any).auth;
      
      // Nueva estructura: users/{userId}/categories/{categoryId}/stock/{SKU}
      const categories = await this.getCategories();
      const allProducts: Product[] = [];
      
      for (const category of categories) {
        const standardizedId = this.getStandardizedId(category.name);
        const productsRef = collection(this.db, 'users', userId, 'categories', standardizedId, 'stock');
        const productsSnapshot = await getDocs(productsRef);
        
        const categoryProducts = productsSnapshot.docs.map((doc: any) => {
          const productData = doc.data();
          
          const variants = productData.variants || [];
          console.log(`🔍 Producto ${doc.id} - Variantes cargadas:`, variants);
          console.log(`🔍 Producto ${doc.id} - Datos completos:`, JSON.stringify(productData, null, 2));
          
          // Log de imágenes de cada variante
          variants.forEach((variant: any, index: number) => {
            console.log(`🖼️ Variante ${index} del producto ${doc.id}:`, {
              color: variant.color,
              images: variant.images,
              hasFrontal: !!variant.images?.frontal,
              hasReverso: !!variant.images?.reverso,
              frontalUrl: variant.images?.frontal,
              reversoUrl: variant.images?.reverso
            });
          });
          
          // Extraer la imagen principal de la primera variante
          let mainImageURL = '';
          if (variants.length > 0 && variants[0].images) {
            const firstVariant = variants[0];
            // Buscar la imagen principal (frontal o reverso)
            if (firstVariant.images.frontal) {
              mainImageURL = firstVariant.images.frontal;
            } else if (firstVariant.images.reverso) {
              mainImageURL = firstVariant.images.reverso;
            }
          }
          
          return {
            id: doc.id,
            sku: doc.id, // El SKU es el ID del documento
            name: productData.name,
            category: category.name,
            brand: productData.marca || productData.brand,
            supplier: productData.proveedor || productData.supplier,
            description: productData.description,
            variants: variants,
            stock: productData.stock || 0,
            priceCost: productData.precioCompra || 0,
            priceSale: productData.precioVenta || 0,
            imageURL: mainImageURL,
            fechaIngreso: productData.fechaIngreso || new Date().toISOString(),
            data: productData.data || {},
            createdAt: productData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            updatedAt: productData.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
          };
        });
        
        allProducts.push(...categoryProducts);
      }
      
      console.log('📦 Productos cargados desde nueva estructura:', allProducts.length);
      return allProducts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } catch (error) {
      console.error('Error cargando productos:', error);
      // Retornar array vacío en lugar de fallback que puede fallar
      return [];
    }
  }

  async getCategoriesFromDocumentedStructure(): Promise<Category[]> {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    
    try {
      // Usar funciones de Firestore desde window.auth
      const { collection, getDocs } = (window as any).auth;
      
      console.log('🔍 Debugging estructura de categorías para userId:', userId);
      console.log('🔍 Probando ruta: users/', userId, '/categories');
      
      // Usar la estructura correcta encontrada
      const categoriesRef = collection(this.db, 'users', userId, 'categories');
      const categoriesSnapshot = await getDocs(categoriesRef);
      
      console.log('📊 Encontrados', categoriesSnapshot.docs.length, 'documentos en users/', userId, '/categories');
      
      if (categoriesSnapshot.docs.length > 0) {
        console.log('📋 Primeros 3 documentos encontrados:');
        categoriesSnapshot.docs.slice(0, 3).forEach((doc: any, index: number) => {
          console.log(`  ${index + 1}. ID: ${doc.id}, Data:`, doc.data());
        });
      }
      
      const categories = categoriesSnapshot.docs.map((doc: any) => ({
        id: doc.id,
        ...doc.data()
      } as Category));
      
      console.log('📂 Categorías cargadas desde estructura documentada:', categories.length);
      return categories;
    } catch (error) {
      console.error('Error cargando categorías desde estructura documentada:', error);
      // Fallback a estructura plana si falla
      return this.getCategories();
    }
  }

  async getBrandsFromDocumentedStructure(): Promise<Brand[]> {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    
    try {
      // Usar funciones de Firestore desde window.auth
      const { collection, getDocs } = (window as any).auth;
      
      console.log('🔍 Debugging estructura de marcas para userId:', userId);
      
      // Intentar diferentes estructuras posibles para marcas
      const possibleBrandPaths = [
        ['users', userId, 'brands'],
        ['users', userId, 'marcas'],
        ['users', userId, 'brand'],
        ['users', userId, 'marca']
      ];
      
      for (const path of possibleBrandPaths) {
        try {
          console.log('🔍 Probando ruta de marcas:', path.join('/'));
          const brandsRef = collection(this.db, ...path);
          const brandsSnapshot = await getDocs(brandsRef);
          console.log(`📊 Encontradas ${brandsSnapshot.docs.length} marcas en ${path.join('/')}`);
          
          if (brandsSnapshot.docs.length > 0) {
            const brands = brandsSnapshot.docs.map((doc: any) => ({
              id: doc.id,
              ...doc.data()
            } as Brand));
            
            console.log('🏷️ Marcas cargadas desde estructura:', path.join('/'), brands.length);
            return brands;
          }
        } catch (pathError) {
          console.log(`❌ Error en ruta de marcas ${path.join('/')}:`, pathError);
        }
      }
      
      console.log('⚠️ No se encontraron marcas en ninguna estructura probada');
      return [];
    } catch (error) {
      console.error('Error cargando marcas desde estructura documentada:', error);
      // Fallback a método original si falla
      return this.getBrands();
    }
  }

  async getSuppliersFromDocumentedStructure(): Promise<Supplier[]> {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    
    try {
      // Usar funciones de Firestore desde window.auth
      const { collection, getDocs } = (window as any).auth;
      
      console.log('🔍 Debugging estructura de proveedores para userId:', userId);
      
      // Intentar diferentes estructuras posibles para proveedores
      const possibleSupplierPaths = [
        ['users', userId, 'suppliers'],
        ['users', userId, 'proveedores'],
        ['users', userId, 'supplier'],
        ['users', userId, 'providers']
      ];
      
      for (const path of possibleSupplierPaths) {
        try {
          console.log('🔍 Probando ruta de proveedores:', path.join('/'));
          const suppliersRef = collection(this.db, ...path);
          const suppliersSnapshot = await getDocs(suppliersRef);
          console.log(`📊 Encontrados ${suppliersSnapshot.docs.length} proveedores en ${path.join('/')}`);
          
          if (suppliersSnapshot.docs.length > 0) {
            const suppliers = suppliersSnapshot.docs.map((doc: any) => ({
              id: doc.id,
              ...doc.data()
            } as Supplier));
            
            console.log('🚚 Proveedores cargados desde estructura:', path.join('/'), suppliers.length);
            return suppliers;
          }
        } catch (pathError) {
          console.log(`❌ Error en ruta de proveedores ${path.join('/')}:`, pathError);
        }
      }
      
      console.log('⚠️ No se encontraron proveedores en ninguna estructura probada');
      return [];
    } catch (error) {
      console.error('Error cargando proveedores desde estructura documentada:', error);
      // Fallback a método original si falla
      return this.getSuppliers();
    }
  }

  // ===== GESTIÓN DE PRODUCTOS =====
  async addProduct(productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt' | 'sku'>): Promise<string> {
    this.ensureFirebaseInitialized();
    const productsRef = this.getUserCollection('products');
    
    // Generar SKU automático
    const sku = await this.generateSKU(productData.category);
    
    const docRef = await this.addDoc(productsRef, {
      ...productData,
      sku,
      createdAt: this.serverTimestamp(),
      updatedAt: this.serverTimestamp()
    });
    
    return docRef.id;
  }

  async getProducts(): Promise<Product[]> {
    this.ensureFirebaseInitialized();
    const productsRef = this.collection(this.db, 'users', this.getUserId(), 'products');
    const snapshot = await this.getDocs(productsRef);
    
    const products = snapshot.docs.map((doc: any) => ({
      id: doc.id,
      ...doc.data()
    } as Product));
    
    // Ordenar manualmente por fecha de creación
    return products.sort((a: Product, b: Product) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    });
  }

  async getLowStockProducts(threshold: number = 5): Promise<Product[]> {
    this.ensureFirebaseInitialized();
    
    try {
      // Usar la nueva estructura documentada
      const allProducts = await this.getProductsFromDocumentedStructure();
      
      // Filtrar productos con stock bajo
      return allProducts
        .filter((product: Product) => (product.stock || 0) <= threshold)
        .sort((a: Product, b: Product) => (a.stock || 0) - (b.stock || 0));
    } catch (error) {
      console.error('❌ Error obteniendo productos con stock bajo:', error);
      return [];
    }
  }

  async updateProduct(productId: string, productData: Partial<Product>): Promise<void> {
    this.ensureFirebaseInitialized();
    const productRef = this.doc(this.db, 'users', this.getUserId(), 'products', productId);
    await this.updateDoc(productRef, {
      ...productData,
      updatedAt: this.serverTimestamp()
    });
  }

  /**
   * Actualizar producto con variantes e imágenes usando la estructura documentada
   */
  async updateProductWithVariants(
    sku: string, 
    categoryName: string, 
    productData: any, 
    variants: any[]
  ): Promise<void> {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    const standardizedId = this.getStandardizedId(categoryName);
    
    try {
      // Usar funciones de Firestore desde window.auth
      const { doc, updateDoc, serverTimestamp } = (window as any).auth;
      
      // Estructura: users/{userId}/categories/{categoryId}/stock/{SKU}
      const productRef = doc(this.db, 'users', userId, 'categories', standardizedId, 'stock', sku);
      
      // Preparar datos del producto con variantes
      const updateData = {
        ...productData,
        variants: variants,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(productRef, updateData);
      console.log(`✅ Producto "${sku}" actualizado con variantes en estructura documentada`);
    } catch (error) {
      console.error('❌ Error actualizando producto con variantes:', error);
      throw error;
    }
  }

  /**
   * Subir imagen a Firebase Storage
   */
  async uploadProductImage(
    file: File,
    sku: string,
    categoryName: string,
    color: string,
    imageType: 'frontal' | 'reverso' | 'closeup1' | 'closeup2' | 'closeup3' | 'closeup4'
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    const standardizedId = this.getStandardizedId(categoryName);
    
    try {
      // Usar funciones de Storage desde window.auth
      const { ref, uploadBytes, getDownloadURL } = (window as any).auth;
      
      // Mapear imageType a la estructura de Firestore
      const firestoreImageType = imageType === 'frontal' ? 'front' : 
                               imageType === 'reverso' ? 'reverse' : 
                               imageType;
      
      // Crear nombre de archivo consistente (sin timestamp para sobreescribir)
      const fileName = `${sku}_${color}_${firestoreImageType}`;
      const imagePath = `users/${userId}/categories/${standardizedId}/stock/${sku}/${fileName}`;
      
      const imageRef = ref(this.storage, imagePath);
      
      // Subir archivo (sobreescribirá si existe)
      await uploadBytes(imageRef, file);
      
      // Obtener URL de descarga
      const downloadURL = await getDownloadURL(imageRef);
      
      console.log(`✅ Imagen sobreescrita: ${imagePath}`);
      
      return {
        success: true,
        url: downloadURL
      };
    } catch (error) {
      console.error('❌ Error subiendo imagen:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error desconocido'
      };
    }
  }

  async deleteProduct(productId: string): Promise<void> {
    this.ensureFirebaseInitialized();
    const { doc, deleteDoc, collection, getDocs } = (window as any).auth;
    
    try {
      const userId = this.getUserId();
      
      // Buscar el producto en todas las categorías (nueva estructura)
      const categories = await this.getCategories();
      let productFound = false;
      
      for (const category of categories) {
        const standardizedId = this.getStandardizedId(category.name);
        const productsRef = collection(this.db, 'users', userId, 'categories', standardizedId, 'stock');
        const productsSnapshot = await getDocs(productsRef);
        
        // Verificar si el producto existe en esta categoría
        const productDoc = productsSnapshot.docs.find((doc: any) => doc.id === productId);
        if (productDoc) {
          // Eliminar el producto de la nueva estructura
          const productRef = doc(this.db, 'users', userId, 'categories', standardizedId, 'stock', productId);
          await deleteDoc(productRef);
          console.log(`✅ Producto ${productId} eliminado de categoría ${category.name}`);
          productFound = true;
          break;
        }
      }
      
      if (!productFound) {
        // Fallback: intentar eliminar de la estructura antigua
        console.log(`⚠️ Producto ${productId} no encontrado en nueva estructura, intentando estructura antigua...`);
        const productRef = doc(this.db, 'users', userId, 'products', productId);
        await deleteDoc(productRef);
        console.log(`✅ Producto ${productId} eliminado de estructura antigua`);
      }
      
    } catch (error) {
      console.error('❌ Error eliminando producto:', error);
      throw error;
    }
  }

  // ===== ESTADÍSTICAS DEL INVENTARIO =====
  async getInventoryStats(): Promise<InventoryStats> {
    this.ensureFirebaseInitialized();
    const [products, categories, brands, suppliers, lowStockProducts] = await Promise.all([
      this.getProductsFromDocumentedStructure(), // Usar la nueva estructura
      this.getCategories(),
      this.getBrands(),
      this.getSuppliers(),
      this.getLowStockProducts()
    ]);

    const totalValue = products.reduce((sum, product) => sum + (product.priceSale * product.stock), 0);

    // Calcular categorías más populares
    const categoryStats = products.reduce((acc, product) => {
      const category = product.category;
      if (!acc[category]) {
        acc[category] = { count: 0, value: 0 };
      }
      acc[category].count += 1;
      acc[category].value += product.priceSale * product.stock;
      return acc;
    }, {} as Record<string, { count: number; value: number }>);
    
    const topCategories = Object.entries(categoryStats)
      .map(([name, data]) => ({ name, count: data.count, value: data.value }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Preparar productos con stock bajo para mostrar
    const lowStockItems = lowStockProducts.map(product => ({
      name: product.name,
      sku: product.sku,
      stock: product.stock,
      category: product.category
    }));

    return {
      totalProducts: products.length,
      totalValue,
      lowStockProducts: lowStockProducts.length,
      categoriesCount: categories.length,
      brandsCount: brands.length,
      suppliersCount: suppliers.length,
      lastUpdated: this.serverTimestamp(),
      topCategories,
      lowStockItems,
      recentActivity: [] // Por ahora vacío, se puede implementar después
    };
  }

  // ===== BÚSQUEDA DE PRODUCTOS =====
  async searchProducts(searchTerm: string): Promise<Product[]> {
    this.ensureFirebaseInitialized();
    const productsRef = this.collection(this.db, 'users', this.getUserId(), 'products');
    const q = this.query(productsRef, this.orderBy('name'));
    const snapshot = await this.getDocs(q);
    
    const allProducts = snapshot.docs.map((doc: any) => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    // Filtrar por término de búsqueda (nombre, SKU, categoría)
    return allProducts.filter((product: Product) => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // ===== SUSCRIPCIONES EN TIEMPO REAL =====
  subscribeToProducts(callback: (products: Product[]) => void): () => void {
    this.ensureFirebaseInitialized();
    const productsRef = this.collection(this.db, 'users', this.getUserId(), 'products');
    const q = this.query(productsRef, this.orderBy('createdAt', 'desc'));
    
    // TODO: Implementar onSnapshot cuando esté disponible en window.auth
    console.warn('⚠️ subscribeToProducts: onSnapshot no está disponible, usando getDocs en su lugar');
    
    // Fallback: usar getDocs en lugar de onSnapshot
    this.getDocs(q).then((snapshot: any) => {
      const products = snapshot.docs.map((doc: any) => ({
        id: doc.id,
        ...doc.data()
      } as Product));
      callback(products);
    });
    
    // Retornar función de limpieza vacía
    return () => {};
  }

  subscribeToLowStockProducts(callback: (products: Product[]) => void, threshold: number = 5): () => void {
    this.ensureFirebaseInitialized();
    const productsRef = this.collection(this.db, 'users', this.getUserId(), 'products');
    const q = this.query(productsRef, this.where('stock', '<=', threshold), this.orderBy('stock', 'asc'));
    
    // TODO: Implementar onSnapshot cuando esté disponible en window.auth
    console.warn('⚠️ subscribeToLowStockProducts: onSnapshot no está disponible, usando getDocs en su lugar');
    
    // Fallback: usar getDocs en lugar de onSnapshot
    this.getDocs(q).then((snapshot: any) => {
      const products = snapshot.docs.map((doc: any) => ({
        id: doc.id,
        ...doc.data()
      } as Product));
      callback(products);
    });
    
    // Retornar función de limpieza vacía
    return () => {};
  }

  // ===== GENERACIÓN DE SKU =====
  private async generateSKU(category: string): Promise<string> {
    this.ensureFirebaseInitialized();
    const productsRef = this.getUserCollection('products');
    const snapshot = await productsRef.where('category', '==', category).get();
    
    const count = snapshot.size + 1;
    const categoryPrefix = this.getCategoryPrefix(category);
    return `${categoryPrefix}${count.toString().padStart(4, '0')}`;
  }

  private getCategoryPrefix(category: string): string {
    const prefixes: Record<string, string> = {
      'Campera': 'CMP',
      'Buzo': 'BUZ',
      'Camisa': 'CAM',
      'Remera': 'REM',
      'Pantalón': 'PAN',
      'Pollera': 'POL',
      'Pañuelo': 'PAÑ',
      'Sombrero/Gorro/Gorra': 'SOM',
      'Calzado': 'CAL'
    };
    
    return prefixes[category] || 'PRD';
  }

  // ===== INICIALIZACIÓN DE DATOS POR DEFECTO =====
  async initializeDefaultData(): Promise<void> {
    this.ensureFirebaseInitialized();
    const userId = this.getUserId();
    
    // Verificar si ya existen datos
    const categories = await this.getCategories();
    if (categories.length > 0) return; // Ya inicializado

    // Crear categorías por defecto según la guía - NO EDITABLES
    const defaultCategories = [
      {
        name: 'Campera',
        fields: {
          tipo: ['abrigo grueso', 'deportiva', 'rompeviento', 'jean', 'cuero', 'inflable', 'bomber', 'parka', 'impermeable'],
          genero: ['hombre', 'mujer', 'unisex', 'niño', 'niña'],
          talle: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '36--50', 'talle único'],
          colorPrincipal: ['negro', 'azul', 'gris', 'marrón', 'verde', 'rojo', 'beige', 'blanco', 'otros'],
          precioCosto: 'number',
          precioVenta: 'number',
          stock: 'integer'
        },
        enabled: true
      },
      {
        name: 'Buzo',
        fields: {
          tipo: ['con capucha', 'sin capucha', 'canguro', 'cerrado', 'cuello redondo', 'medio cierre'],
          materialPrincipal: ['algodón', 'frisa', 'poliéster', 'fleece', 'mezcla'],
          estilo: ['casual', 'urbano', 'deportivo', 'streetwear'],
          genero: ['hombre', 'mujer', 'unisex', 'niño', 'niña'],
          talle: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '36--50', 'talle único'],
          colorPrincipal: ['negro', 'azul', 'gris', 'marrón', 'verde', 'rojo', 'beige', 'blanco', 'otros'],
          precioCosto: 'number',
          precioVenta: 'number',
          stock: 'integer'
        },
        enabled: true
      },
      {
        name: 'Camisa',
        fields: {
          tipo: ['formal', 'casual', 'deportiva', 'camiseta', 'polo'],
          materialPrincipal: ['algodón', 'lino', 'poliéster', 'mezcla'],
          genero: ['hombre', 'mujer', 'unisex', 'niño', 'niña'],
          talle: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '36--50', 'talle único'],
          colorPrincipal: ['negro', 'azul', 'gris', 'marrón', 'verde', 'rojo', 'beige', 'blanco', 'otros'],
          precioCosto: 'number',
          precioVenta: 'number',
          stock: 'integer'
        },
        enabled: true
      },
      {
        name: 'Remera',
        fields: {
          tipo: ['básica', 'estampada', 'deportiva', 'oversize', 'crop top'],
          materialPrincipal: ['algodón', 'poliéster', 'mezcla'],
          genero: ['hombre', 'mujer', 'unisex', 'niño', 'niña'],
          talle: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '36--50', 'talle único'],
          colorPrincipal: ['negro', 'azul', 'gris', 'marrón', 'verde', 'rojo', 'beige', 'blanco', 'otros'],
          precioCosto: 'number',
          precioVenta: 'number',
          stock: 'integer'
        },
        enabled: true
      },
      {
        name: 'Pantalón',
        fields: {
          tipo: ['jean', 'cargo', 'deportivo', 'casual', 'formal'],
          materialPrincipal: ['denim', 'algodón', 'poliéster', 'mezcla'],
          genero: ['hombre', 'mujer', 'unisex', 'niño', 'niña'],
          talle: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '36--50', 'talle único'],
          colorPrincipal: ['negro', 'azul', 'gris', 'marrón', 'verde', 'rojo', 'beige', 'blanco', 'otros'],
          precioCosto: 'number',
          precioVenta: 'number',
          stock: 'integer'
        },
        enabled: true
      },
      {
        name: 'Pollera',
        fields: {
          tipo: ['mini', 'midi', 'maxi', 'deportiva', 'formal'],
          materialPrincipal: ['algodón', 'poliéster', 'mezcla'],
          genero: ['mujer', 'niña'],
          talle: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '36--50', 'talle único'],
          colorPrincipal: ['negro', 'azul', 'gris', 'marrón', 'verde', 'rojo', 'beige', 'blanco', 'otros'],
          precioCosto: 'number',
          precioVenta: 'number',
          stock: 'integer'
        },
        enabled: true
      },
      {
        name: 'Pañuelo',
        fields: {
          tipo: ['cuadrado', 'rectangular', 'triangular'],
          materialPrincipal: ['seda', 'algodón', 'poliéster'],
          genero: ['hombre', 'mujer', 'unisex'],
          colorPrincipal: ['negro', 'azul', 'gris', 'marrón', 'verde', 'rojo', 'beige', 'blanco', 'otros'],
          precioCosto: 'number',
          precioVenta: 'number',
          stock: 'integer'
        },
        enabled: true
      },
      {
        name: 'Sombrero/Gorro/Gorra',
        fields: {
          tipo: ['sombrero', 'gorro', 'gorra', 'boina', 'fedora'],
          materialPrincipal: ['lana', 'algodón', 'poliéster', 'cuero'],
          genero: ['hombre', 'mujer', 'unisex', 'niño', 'niña'],
          colorPrincipal: ['negro', 'azul', 'gris', 'marrón', 'verde', 'rojo', 'beige', 'blanco', 'otros'],
          precioCosto: 'number',
          precioVenta: 'number',
          stock: 'integer'
        },
        enabled: true
      },
      {
        name: 'Calzado',
        fields: {
          tipo: ['zapatillas', 'zapatos', 'botas', 'sandalias', 'mocasines'],
          materialPrincipal: ['cuero', 'sintético', 'tela', 'goma'],
          genero: ['hombre', 'mujer', 'unisex', 'niño', 'niña'],
          talle: ['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46'],
          colorPrincipal: ['negro', 'azul', 'gris', 'marrón', 'verde', 'rojo', 'beige', 'blanco', 'otros'],
          precioCosto: 'number',
          precioVenta: 'number',
          stock: 'integer'
        },
        enabled: true
      }
    ];

    // Crear categorías por defecto
    for (const categoryData of defaultCategories) {
      await this.addCategory(categoryData);
    }
  }
}

// Exportar instancia singleton
export const inventoryService = new InventoryService();
export default inventoryService;